import React, { useEffect, useRef, useState } from 'react'
import * as d3 from 'd3'
import dayjs from 'dayjs'
import { Tooltip, TooltipVariant } from '../LineChart/Tooltip'
import { Period } from 'screens/Analytics/constants'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { useSelector } from 'react-redux'
import { RootState } from 'types'
import { PERIOD_DURATIONS } from 'screens/blaze/Home/VirtualMarketerHome/Brain/Metric/utils'
import { MetricSnapshot } from 'hooks/queries/externalIntegrations/types'
import { useIsSystemRedesign } from 'hooks/useIsSystemRedesign'

interface BarChartProps {
  data: MetricSnapshot[]
  height: number
  name: string
  period?: Period
  containerWidth?: number
  color?: string
  withBorder?: boolean
  borderRadius?: number
  fontColor?: string
  showNullValues?: boolean
  tooltipVariant?: TooltipVariant
  visibleTicks?: number
  showEmptyState?: boolean
  isTooltipPercentage?: boolean
  withHover?: boolean
}

interface DataPoint {
  date: Date
  value: number | null // null represents no data for that day
  dateStr: string
}

const BarChart: React.FC<BarChartProps> = ({
  data,
  height,
  name,
  period = Period.LastWeek,
  containerWidth = '100%',
  color = '#0077FF',
  withBorder = true,
  borderRadius = 0,
  fontColor = 'var(--dark-80)',
  showNullValues = false,
  tooltipVariant = 'default',
  visibleTicks,
  showEmptyState = true,
  isTooltipPercentage = false,
  withHover = true,
}) => {
  const isSystemRedesignEnabled = useIsSystemRedesign()
  const svgRef = useRef<SVGSVGElement | null>(null)
  const wrapperRef = useRef<HTMLDivElement | null>(null)
  const gRef = useRef<d3.Selection<SVGGElement, unknown, null, undefined> | null>(null)
  const xAxisGroupRef = useRef<d3.Selection<SVGGElement, unknown, null, undefined> | null>(null)
  const barsGroupRef = useRef<d3.Selection<SVGGElement, unknown, null, undefined> | null>(null)
  const [width, setWidth] = useState<number>(0)
  const { isMobile } = useResponsiveDesign()

  // Tooltip state
  const [tooltipVisible, setTooltipVisible] = useState<boolean>(false)
  const [tooltipValue, setTooltipValue] = useState<{ date: Date; value: number | string } | null>(null)
  const [tooltipPosition, setTooltipPosition] = useState<{ x: number; y: number }>({ x: 0, y: 0 })
  const { sidebarLocked } = useSelector(({ sidebar: { locked: sidebarLocked } }: RootState) => ({ sidebarLocked }))

  // Resize observer for responsive width
  useEffect(() => {
    const handleResize = () => {
      if (wrapperRef.current) {
        setWidth(wrapperRef.current.clientWidth)
      }
    }

    handleResize()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  useEffect(() => {
    if (wrapperRef.current) {
      setTimeout(() => {
        if (wrapperRef.current) {
          setWidth(wrapperRef.current.clientWidth)
        }
      }, 500)
    }
  }, [sidebarLocked])

  useEffect(() => {
    if (width === 0) return

    function wrapText(textSelection: d3.Selection<SVGTextElement, unknown, null, undefined>, width: number): void {
      textSelection.each(function (this: SVGTextElement) {
        const text = d3.select(this)
        const words: string[] = text.text().split(/\s+/).reverse()
        let word: string | undefined
        let line: string[] = []
        let lineNumber = 0
        const lineHeight = 1.1
        const y = text.attr('y')
        const x = text.attr('x')
        const dy = 0

        let tspan = text
          .text(null)
          .append('tspan')
          .attr('x', x !== null ? x : null)
          .attr('y', y !== null ? y : null)
          .attr('dy', `${dy}em`)

        while ((word = words.pop()) !== undefined) {
          line.push(word)
          tspan.text(line.join(' '))

          const node = tspan.node()
          if (node && node.getComputedTextLength() > width) {
            line.pop()
            tspan.text(line.join(' '))
            line = [word]
            tspan = text
              .append('tspan')
              .attr('x', x !== null ? x : null)
              .attr('y', y !== null ? y : null)
              .attr('dy', `${++lineNumber * lineHeight + dy}em`)
              .text(word)
          }
        }
      })
    }

    // Set margins
    const desktopXMargin = isSystemRedesignEnabled && period === Period.LastWeek ? 0 : 10
    const xMargin = isMobile ? 0 : desktopXMargin
    const margin = { top: 10, right: xMargin, bottom: 30, left: xMargin }
    const innerWidth = width - margin.left - margin.right
    const innerHeight = height - margin.top - margin.bottom

    const completeData = data
      ?.map((d) => ({
        dateStr: dayjs(d.recorded_at).format('YYYY-MM-DD'),
        date: dayjs(d.recorded_at).toDate(),
        value: d.metric_value,
      }))
      .slice(-PERIOD_DURATIONS[period])

    // Create scales
    const xScale = d3
      .scaleBand()
      .range([0, innerWidth])
      .padding(0.2)
      .domain(completeData.map((d) => d.dateStr))

    const yScale = d3
      .scaleLinear()
      .range([innerHeight, 0])
      .domain([0, (d3.max(completeData, (d) => (d.value !== null ? d.value : 0)) as number) * 1.05])
      .nice()

    // Create SVG if it doesn't exist
    let svg = d3.select(svgRef.current)
    if (svg.select('g').empty()) {
      svg = svg.attr('width', width).attr('height', height)
      const g = svg.append('g').attr('transform', `translate(${margin.left},${margin.top})`)
      gRef.current = g
      barsGroupRef.current = g.append('g')
      xAxisGroupRef.current = g.append('g').attr('class', 'x-axis').attr('transform', `translate(0,${innerHeight})`)
    } else {
      svg.attr('width', width).attr('height', height)
      gRef.current?.attr('transform', `translate(${margin.left},${margin.top})`)
      xAxisGroupRef.current?.attr('transform', `translate(0,${innerHeight})`)
    }

    // Handle empty data state
    const hasEnoughData = completeData.filter((d) => d.value !== null).length >= 3
    if (!hasEnoughData && showEmptyState) {
      svg.style('background-color', 'rgba(0, 0, 0, 0.03)')
      const noDataText = gRef.current?.selectAll('.no-data-text').data([null])

      const enterSelection = noDataText
        ?.enter()
        .append('text')
        .attr('class', 'no-data-text')
        .attr('text-anchor', 'middle')
        .attr('fill', 'rgba(0, 0, 0, 0.4)')

      const mergedSelection = enterSelection
        ?.merge(noDataText as any)
        .attr('x', innerWidth / 2)
        .attr('y', innerHeight / 2)
        .text('We need at least 3 days of data to show this graph')

      if (isMobile) {
        mergedSelection &&
          wrapText(
            mergedSelection as unknown as d3.Selection<SVGTextElement, unknown, null, undefined>,
            innerWidth * 0.8,
          )
      }
    } else {
      svg.style('background-color', null)
      gRef.current?.select('.no-data-text').remove()

      // Update bars
      const bars = barsGroupRef.current?.selectAll<SVGRectElement, DataPoint>('.bar').data(completeData)

      // Remove old bars
      bars?.exit().remove()

      // Add new bars and update existing ones
      bars
        ?.enter()
        .append('rect')
        .attr('class', 'bar')
        .merge(bars)
        .attr('x', (d) => xScale(d.dateStr) || 0)
        .attr('width', xScale.bandwidth())
        .attr('y', (d) => {
          if (showNullValues && !d.value) return 0
          if (d.value === null) return innerHeight // No bar for null values
          return d.value === 0 ? innerHeight - 2 : yScale(d.value) // 2px height for zero values
        })
        .attr('height', (d) => {
          if (showNullValues && !d.value) return innerHeight
          if (d.value === null) return 0 // No height for null values
          return d.value === 0 ? 2 : innerHeight - yScale(d.value) // 2px height for zero values
        })
        .attr('fill', (d) => (showNullValues && !d.value ? 'transparent' : color))
        .style('opacity', (d) => (d.value === null && !showNullValues ? 0 : 0.7)) // No opacity for null values
        .style('rx', borderRadius)
        .style('ry', borderRadius)
        .style('cursor', withHover ? 'pointer' : 'default')
        .on('mouseover', function (_, d) {
          if (withHover && (d.value !== null || showNullValues)) {
            d3.select(this).style('opacity', 1)
            setTooltipVisible(true)
            setTooltipValue({ date: d.date, value: isTooltipPercentage ? `${d.value || 0}%` : d.value || 0 })

            const barX = parseFloat(d3.select(this).attr('x'))
            const barWidth = parseFloat(d3.select(this).attr('width'))

            let tooltipX = barX + barWidth / 2 + margin.left
            const tooltipWidth = 120
            if (tooltipX + tooltipWidth > width) {
              tooltipX -= tooltipWidth + 20
            }

            setTooltipPosition({
              x: tooltipX,
              y: yScale(d.value || 0) + margin.top - 40,
            })
          }
        })
        .on('mouseout', function () {
          d3.select(this).style('opacity', 0.7)
          setTooltipVisible(false)
        })

      // Update x-axis
      const xAxis = d3
        .axisBottom(xScale)
        .tickFormat((d) => dayjs(d as string).format('MMM D'))
        .tickValues(
          visibleTicks === 3
            ? (() => {
                const domain = xScale.domain()
                const first = domain[0]
                const last = domain[domain.length - 1]
                const middle = domain[Math.floor(domain.length / 2)]
                return [first, middle, last]
              })()
            : xScale
                .domain()
                .filter((_, i) => i % Math.ceil(completeData.length / (visibleTicks || (isMobile ? 3 : 6))) === 0),
        )

      // Add border around the chart area
      svg.selectAll('.chart-border').remove()

      if (withBorder) {
        svg
          .append('path')
          .attr('class', 'chart-border')
          .attr('x', margin.left)
          .attr('y', margin.top)
          .attr('width', innerWidth)
          .attr('height', innerHeight)
          .attr('fill', 'none')
          .attr('stroke', 'rgba(0, 0, 0, 0.04)')
          .attr('stroke-width', 1)
      }

      // Update x-axis with modified domain line
      xAxisGroupRef.current?.transition().duration(0).call(xAxis)

      // Style the domain and tick lines
      xAxisGroupRef.current?.select('.domain').remove()
      xAxisGroupRef.current?.selectAll('.tick line').style('stroke', 'rgba(0, 0, 0, 0)')
      xAxisGroupRef.current?.selectAll('.tick text').style('fill', fontColor)
    }
  }, [
    data,
    height,
    width,
    period,
    isMobile,
    color,
    withBorder,
    borderRadius,
    showNullValues,
    fontColor,
    visibleTicks,
    showEmptyState,
    isTooltipPercentage,
    withHover,
    isSystemRedesignEnabled,
  ])

  return (
    <div ref={wrapperRef} style={{ width: containerWidth, position: 'relative', height }}>
      <svg ref={svgRef} />
      {withHover && tooltipVisible && tooltipValue && (
        <Tooltip
          x={tooltipPosition.x}
          y={tooltipPosition.y}
          value={tooltipValue}
          name={name}
          variant={tooltipVariant}
        />
      )}
    </div>
  )
}

export default BarChart
